import { useAuth, useLocale } from '../../context';
import { formatMoney } from '../../utils/currency';
import MobileAccountNotifications from './MobileAccountNotifications';
import MobileAccountResponsible from './MobileAccountResponsible';
import MobileAccountResponsibleDeposit from './MobileAccountResponsibleDeposit';
import MobileAccountResponsibleSession from './MobileAccountResponsibleSession';
import MobileAccountRequests from './MobileAccountRequests';
import MobileNewRequestForm from './MobileNewRequestForm';
import MobileNewRequestPicker from './MobileNewRequestPicker';
import ProfileContactChange from '../account/ProfileContactChange';
import ProfileKycForm from '../account/ProfileKycForm';
import MobileAccountCashier from './MobileAccountCashier';
import MobileAccountChangePassword from './MobileAccountChangePassword';
import MobileAccountPasskeys from './MobileAccountPasskeys';
import MobileAccountHistory from './MobileAccountHistory';
import MobileAccountProfile from './MobileAccountProfile';
import { hasUserEmail, hasUserPhone } from '../../utils/userDisplay';

const VIEW_TITLES = {
  cashier: 'account.menu.payments',
  bets: 'account.menu.history',
  support: 'account.menu.requests',
  notifications: 'account.nav.notifications',
  profile: 'account.nav.profile',
  security: 'account.menu.responsible',
  'security-deposit': 'account.responsible.tabDeposit',
  'security-session': 'account.responsible.timeLimitsTitle',
  'support-new': 'account.requests.newRequest',
  'support-form': 'account.requests.newRequest',
  'profile-password': 'account.profile.tab.password',
  'profile-passkeys': 'account.profile.tab.passkeys',
  'profile-phone': 'account.profile.changePhoneTitle',
  'profile-email': 'account.profile.changeEmailTitle',
  'profile-kyc': 'account.profile.kyc.title',
};

function phoneSheetTitleKey(summary) {
  if (!hasUserPhone(summary?.phone)) return 'account.profile.addPhoneTitle';
  if (!summary?.phone_verified) return 'account.profile.verifyPhoneTitle';
  return 'account.profile.changePhoneTitle';
}

function emailSheetTitleKey(summary) {
  if (!hasUserEmail(summary?.email)) return 'account.profile.addEmailTitle';
  if (!summary?.email_verified) return 'account.profile.verifyEmailTitle';
  return 'account.profile.changeEmailTitle';
}

export function getSheetViewTitleKey(view, summary) {
  if (view.id === 'cashier') {
    if (view.tab === 'deposit' && !view.methodId) return 'account.payments.selectMethod';
    if (view.tab === 'deposit') return 'account.cashier.deposit';
    return 'account.cashier.withdraw';
  }
  if (view.id === 'profile-phone') return phoneSheetTitleKey(summary);
  if (view.id === 'profile-email') return emailSheetTitleKey(summary);
  return VIEW_TITLES[view.id] || 'account.menu.profile';
}

export function sheetViewShowsBalance(view) {
  return view.id === 'cashier' || view.id === 'bets';
}

export default function MobileAccountSheetView({
  view,
  summary,
  onSummaryUpdated,
  onProfileUpdated,
  onCashierDeposit,
  onOpenSupport,
  onGoHelp,
  onCreateRequest,
  onSelectRequestTemplate,
  onRequestDone,
  onRequestCancel,
  onNavigateProfilePanel,
  onProfilePasswordDone,
  onOpenResponsibleDeposit,
  onOpenResponsibleSession,
  onNavigate,
  onOpenSheetView,
  onSelectDepositMethod,
  onClearDepositMethod,
}) {
  const _locale = useLocale();
  const tr = _locale.t;
  const { token } = useAuth();
  const phonePresent = hasUserPhone(summary?.phone);
  const emailPresent = hasUserEmail(summary?.email);
  const phoneVerified = Boolean(summary?.phone_verified);
  const emailVerified = Boolean(summary?.email_verified);
  const phoneMode = !phonePresent ? 'add' : (phoneVerified ? 'change' : 'verify');
  const emailMode = !emailPresent ? 'add' : (emailVerified ? 'change' : 'verify');

  if (view.id === 'cashier') {
    return (
      <MobileAccountCashier
        summary={summary}
        initialTab={view.tab || 'deposit'}
        selectedMethodId={view.methodId || null}
        onSelectMethod={onSelectDepositMethod}
        onClearMethod={onClearDepositMethod}
        onUpdated={onSummaryUpdated}
        onDeposit={onCashierDeposit}
      />
    );
  }

  const phoneTitle = tr(phoneSheetTitleKey(summary));
  const emailTitle = tr(emailSheetTitleKey(summary));

  const section = (
    <>
      {view.id === 'profile' && (
        <MobileAccountProfile summary={summary} onOpenPanel={onNavigateProfilePanel} />
      )}
      {view.id === 'profile-password' && (
        <MobileAccountChangePassword onDone={onProfilePasswordDone} />
      )}
      {view.id === 'profile-passkeys' && (
        <MobileAccountPasskeys />
      )}
      {view.id === 'profile-kyc' && (
        <div className="mobile-account-profile">
          <ProfileKycForm
            title={tr('account.profile.kyc.title')}
            summary={summary}
            token={token}
            onDone={() => {
              onSummaryUpdated?.();
              onProfileUpdated?.();
            }}
            t={tr}
          />
        </div>
      )}
      {view.id === 'profile-phone' && (
        <div className="mobile-account-profile">
          <ProfileContactChange
            type="phone"
            mode={phoneMode}
            title={phoneTitle}
            initialValue={phonePresent ? summary.phone : ''}
            token={token}
            onDone={() => {
              onSummaryUpdated?.();
              onProfileUpdated?.();
            }}
            t={tr}
          />
        </div>
      )}
      {view.id === 'profile-email' && (
        <div className="mobile-account-profile">
          <ProfileContactChange
            type="email"
            mode={emailMode}
            title={emailTitle}
            initialValue={emailPresent ? summary.email : ''}
            token={token}
            onDone={() => {
              onSummaryUpdated?.();
              onProfileUpdated?.();
            }}
            t={tr}
          />
        </div>
      )}
      {view.id === 'bets' && <MobileAccountHistory />}
      {view.id === 'notifications' && (
        <MobileAccountNotifications
          onRead={onSummaryUpdated}
          onNavigate={onNavigate}
          onOpenSheetView={onOpenSheetView}
        />
      )}
      {view.id === 'security' && (
        <MobileAccountResponsible
          onOpenDeposit={onOpenResponsibleDeposit}
          onOpenSession={onOpenResponsibleSession}
        />
      )}
      {view.id === 'security-deposit' && <MobileAccountResponsibleDeposit />}
      {view.id === 'security-session' && <MobileAccountResponsibleSession />}
      {view.id === 'support' && (
        <MobileAccountRequests onGoHelp={onGoHelp} onCreateRequest={onCreateRequest} />
      )}
      {view.id === 'support-new' && (
        <MobileNewRequestPicker onSelect={onSelectRequestTemplate} />
      )}
      {view.id === 'support-form' && (
        <MobileNewRequestForm
          templateId={view.templateId}
          onDone={onRequestDone}
          onCancel={onRequestCancel}
        />
      )}
    </>
  );

  return (
    <div className="mobile-account-sheet__section">
      {section}
    </div>
  );
}

export function formatSheetBalance(balance, tr) {
  return tr('mobile.account.balance', { amount: formatMoney(balance) });
}
